import type { ReactNode } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  type SxProps,
  type Theme
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useRecordContext, useResourceContext } from 'react-admin';

const recordSectionSx: SxProps<Theme> = {
  p: { xs: 1.75, sm: 2 },
  borderRadius: 1.2,
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider'
};

export const RecordPage = ({ children }: { children: ReactNode }) => (
  <Box sx={{ pb: 4 }}>{children}</Box>
);

type RecordHeaderProps = {
  avatar: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  badges?: ReactNode;
  quickActions?: ReactNode;
  actions?: ReactNode;
};

export const RecordHeader = ({
  avatar,
  title,
  subtitle,
  badges,
  quickActions,
  actions
}: RecordHeaderProps) => (
  <Paper
    elevation={0}
    sx={{
      ...recordSectionSx,
      p: { xs: 1.75, sm: 2.25 },
      borderRadius: 1.5,
      mb: 1.5,
      boxShadow: '0 1px 2px rgba(16, 24, 40, 0.03)'
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap'
      }}
    >
      {avatar}
      <Box sx={{ flex: 1, minWidth: 180 }}>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: '1.2rem', sm: '1.4rem' },
            lineHeight: 1.25,
            overflowWrap: 'anywhere'
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
            {subtitle}
          </Typography>
        ) : null}
        {badges ? (
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {badges}
          </Stack>
        ) : null}
        {quickActions ? <Box sx={{ mt: 1.1 }}>{quickActions}</Box> : null}
      </Box>
      {actions ? (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ ml: { xs: 0, sm: 'auto' }, flexWrap: 'wrap' }}
        >
          {actions}
        </Stack>
      ) : null}
    </Box>
  </Paper>
);

type RecordSectionProps = {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  sx?: SxProps<Theme>;
};

export const RecordSection = ({ title, description, action, children, sx }: RecordSectionProps) => (
  <Box sx={[recordSectionSx, ...(Array.isArray(sx) ? sx : [sx])]}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        mb: 1.25
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

export const DetailRow = ({
  icon,
  label,
  children
}: {
  icon?: ReactNode;
  label: ReactNode;
  children: ReactNode;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.9 }}>
    {icon ? (
      <Box sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0, lineHeight: 0 }}>{icon}</Box>
    ) : null}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.2 }}>{children}</Box>
    </Box>
  </Box>
);

export const RecordEditButton = ({
  editPath,
  returnTab = 'overview',
  label = 'Edit record',
  variant = 'contained'
}: {
  editPath?: string;
  returnTab?: string;
  label?: string;
  variant?: 'contained' | 'text' | 'outlined';
}) => {
  const resource = useResourceContext();
  const record = useRecordContext<{ id?: string | number }>();
  if (!resource || !record?.id) return null;

  const suffix = editPath ? `/${editPath}` : '';
  const target = `/${resource}/${record.id}${suffix}?returnTab=${encodeURIComponent(returnTab)}`;

  if (variant === 'text' && label === 'Edit') {
    return (
      <Tooltip title="Edit section">
        <IconButton
          component={RouterLink}
          to={target}
          size="small"
          color="primary"
          aria-label="Edit section"
          sx={{ mt: -0.5, mr: -0.5 }}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      component={RouterLink}
      to={target}
      startIcon={<EditOutlinedIcon />}
      variant={variant}
      size="small"
      color="primary"
    >
      {label}
    </Button>
  );
};

export type RecordTab = {
  value: string;
  label: string;
  content: ReactNode;
};

export const RecordTabs = ({
  tabs,
  defaultValue = 'overview'
}: {
  tabs: RecordTab[];
  defaultValue?: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedValue = searchParams.get('tab');
  const value = tabs.some((tab) => tab.value === requestedValue) ? requestedValue! : defaultValue;

  const handleChange = (_event: React.SyntheticEvent, nextValue: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextValue === defaultValue) {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', nextValue);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const activeTab = tabs.find((tab) => tab.value === value) ?? tabs[0];

  return (
    <Box>
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: 1.5,
          overflowX: 'auto'
        }}
      >
        <Tabs
          value={activeTab.value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Record sections"
        >
          {tabs.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      <Box role="tabpanel" aria-label={activeTab.label}>
        {activeTab.content}
      </Box>
    </Box>
  );
};

export const EmptyState = ({ children }: { children: ReactNode }) => (
  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
    {children}
  </Typography>
);
